/**
 * Login, logout, token refresh, and current-user fetch; persists JWTs via `client` helpers.
 *
 * Where: `Login` page, `useAuth`, and any explicit session refresh.
 * Uses: `apiClient` + `setTokens` / `clearTokens` from `./client`.
 * Contract: POST `/token/` → `response.data` is `{ access, refresh, user }` (not wrapped in `output`); POST `/token/refresh/` → `{ access, ... }`; GET `/user/profile/` → `{ output: User }` and `getCurrentUser` returns `response.data.output`.
 * Behavior: `login` persists tokens via `setTokens`; `logout` only clears client tokens (no server call here).
 * Invariants: token refresh for expired access is primarily handled in `client.ts` interceptors, not this module.
 */

import { apiClient, setTokens, clearTokens } from './client';
import type {
  LoginRequest,
  LoginResponse,
  TokenRefreshResponse,
  User,
} from '@/types/auth';

export const authApi = {
  /**
   * Login user
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/token/', credentials);
    const { access, refresh, user } = response.data;
    
    // Store tokens
    setTokens(access, refresh);
    
    return { access, refresh, user };
  },

  /**
   * Logout user
   */
  logout: async (): Promise<void> => {
    clearTokens();
  },

  /**
   * Refresh access token
   */
  refreshToken: async (): Promise<string> => {
    const response = await apiClient.post<TokenRefreshResponse>('/token/refresh/');
    return response.data.access;
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<{ output: User }>('/user/profile/');
    return response.data.output;
  },
};
